<template>
  <div class="pdp-panel">
    <!-- Empty state: no prizeDistribution configured yet -->
    <template v-if="!localDist">
      <p class="pdp-empty-note">
        No prize budget configured. Enter total prizes to enable distribution gate.
        (Unlimited until set.)
      </p>
      <div class="pdp-init-row">
        <label class="pdp-label" :for="`pdp-init-total-${roundId}`">
          Total prizes
          <input
            :id="`pdp-init-total-${roundId}`"
            v-model.number="initTotal"
            type="number"
            min="0"
            max="10000"
            step="1"
            class="pdp-input pdp-input--short"
            placeholder="e.g. 30"
          />
        </label>
        <button
          type="button"
          class="primary-button primary-button-small"
          :disabled="saving || initTotal < 1"
          @click="enableDistribution"
        >
          {{ saving ? 'Saving…' : 'Enable' }}
        </button>
      </div>
      <p v-if="saveError" class="pdp-error">{{ saveError }}</p>
    </template>

    <!-- Configured state -->
    <template v-else>
      <!-- Edit fields -->
      <div class="pdp-fields">
        <label class="pdp-label" :for="`pdp-total-${roundId}`">
          Total prizes
          <input
            :id="`pdp-total-${roundId}`"
            v-model.number="localDist.totalPrizes"
            type="number"
            min="0"
            max="10000"
            step="1"
            class="pdp-input pdp-input--short"
          />
        </label>

        <div class="pdp-label">
          Window
          <span class="pdp-window-display">
            {{ formatWindowDate(roundStartDate) }}
            &nbsp;→&nbsp;
            {{ formatWindowDate(roundEndDate) }}
          </span>
          <span class="pdp-field-note">(read-only — set via round dates)</span>
        </div>

        <label class="pdp-label" :for="`pdp-bucket-${roundId}`">
          Bucket size
          <select
            :id="`pdp-bucket-${roundId}`"
            v-model.number="localDist.bucketSeconds"
            class="pdp-input"
          >
            <option :value="86400">24 hours</option>
            <option :value="43200">12 hours</option>
            <option :value="21600">6 hours</option>
            <option :value="3600">1 hour</option>
            <option :value="300">5 minutes — dev test</option>
            <option :value="60">1 minute — dev test</option>
          </select>
        </label>

        <label class="pdp-label" :for="`pdp-abandon-${roundId}`">
          Abandonment timeout
          <select
            :id="`pdp-abandon-${roundId}`"
            v-model.number="localDist.abandonmentTimeoutSeconds"
            class="pdp-input"
          >
            <option :value="600">10 minutes</option>
            <option :value="60">60 seconds — dev test</option>
            <option :value="30">30 seconds — dev test</option>
            <option :value="10">10 seconds — dev test</option>
          </select>
        </label>
      </div>

      <!-- Live today panel -->
      <div class="pdp-live-section">
        <div class="pdp-live-header">
          <span class="pdp-live-title">Today</span>
          <button
            type="button"
            class="pdp-refresh-btn"
            :disabled="statusLoading"
            @click="refreshStatus"
          >
            {{ statusLoading ? '…' : 'Refresh' }}
          </button>
        </div>

        <template v-if="status">
          <p class="pdp-bucket-label">
            Bucket {{ formatWindowDate(status.currentBucket.bucketStart) }} →
          </p>
          <div class="pdp-live-row">
            <span>Issued:</span>
            <strong>{{ status.currentBucket.wins }} / {{ status.currentBucket.cap }}</strong>
          </div>
          <div class="pdp-live-row">
            <span>Remaining today:</span>
            <strong>{{ status.currentBucket.remaining }}</strong>
          </div>
          <div class="pdp-live-row pdp-live-row--muted">
            <span>Round total remaining:</span>
            <strong>{{ status.round.prizesRemaining }}</strong>
          </div>

          <!-- Recent buckets mini-chart -->
          <div v-if="status.recentBuckets.length" class="pdp-recent">
            <span class="pdp-recent-title">Recent buckets</span>
            <div
              v-for="bucket in status.recentBuckets"
              :key="bucket.bucketStart"
              class="pdp-recent-row"
            >
              <span class="pdp-recent-date">{{ formatBucketDate(bucket.bucketStart) }}</span>
              <span class="pdp-recent-bar" :aria-label="`${bucket.wins} of ${bucket.cap}`">
                <span
                  class="pdp-recent-fill"
                  :style="{ width: bucket.cap > 0 ? `${Math.round((bucket.wins / bucket.cap) * 100)}%` : '0%' }"
                />
              </span>
              <span class="pdp-recent-count">{{ bucket.wins }}/{{ bucket.cap }}</span>
            </div>
          </div>
        </template>

        <p v-else-if="statusLoading" class="pdp-status-note">Loading…</p>
        <p v-else-if="statusError" class="pdp-status-note pdp-status-note--dim">
          {{ statusError }}
        </p>
        <p v-else class="pdp-status-note pdp-status-note--dim">—</p>
      </div>

      <p v-if="saveError" class="pdp-error">{{ saveError }}</p>
      <p v-if="saveToast" class="pdp-toast">{{ saveToast }}</p>

      <div class="pdp-actions">
        <button
          type="button"
          class="primary-button primary-button-small"
          :disabled="saving"
          @click="emit('save', localDist)"
        >
          {{ saving ? 'Saving…' : 'Save changes' }}
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onUnmounted,
  type PropType,
} from 'vue'
import { campaignsService, type PrizeBudgetStatus } from '@/services/campaigns'
import type { PrizeDistribution } from '@/services/rounds'

export default defineComponent({
  name: 'PrizeDistributionPanel',

  props: {
    campaignId: {
      type: String,
      required: true,
    },
    roundId: {
      type: String,
      required: true,
    },
    roundIndex: {
      type: Number,
      required: true,
    },
    roundStartDate: {
      type: String,
      default: '',
    },
    roundEndDate: {
      type: String,
      default: '',
    },
    prizeDistribution: {
      type: Object as PropType<PrizeDistribution | null | undefined>,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    saveError: {
      type: String,
      default: '',
    },
    saveToast: {
      type: String,
      default: '',
    },
  },

  emits: {
    save: (_dist: PrizeDistribution) => true,
  },

  setup(props, { emit }) {
    // Deep-clone so local edits don't mutate the parent
    const localDist = ref<PrizeDistribution | null>(
      props.prizeDistribution ? { ...props.prizeDistribution } : null,
    )

    const initTotal = ref(30)

    const status = ref<PrizeBudgetStatus | null>(null)
    const statusLoading = ref(false)
    const statusError = ref<string | null>(null)

    let autoRefreshTimer: number | null = null

    const refreshStatus = async () => {
      if (!localDist.value) return
      statusLoading.value = true
      statusError.value = null
      try {
        status.value = await campaignsService.getPrizeBudgetStatus(
          props.campaignId,
          props.roundId,
        )
      } catch (err: unknown) {
        if (
          err &&
          typeof err === 'object' &&
          'response' in err &&
          (err as { response?: { status?: number } }).response?.status === 404
        ) {
          status.value = null
          statusError.value = null
        } else {
          statusError.value = 'Unable to load live count.'
        }
      } finally {
        statusLoading.value = false
      }
    }

    const startAutoRefresh = () => {
      stopAutoRefresh()
      if (!localDist.value) return
      autoRefreshTimer = window.setInterval(() => {
        void refreshStatus()
      }, 30_000)
    }

    const stopAutoRefresh = () => {
      if (autoRefreshTimer !== null) {
        clearInterval(autoRefreshTimer)
        autoRefreshTimer = null
      }
    }

    const enableDistribution = () => {
      const winStart = props.roundStartDate || new Date().toISOString()
      const winEnd = props.roundEndDate || new Date(Date.now() + 7 * 86400 * 1000).toISOString()
      localDist.value = {
        totalPrizes: initTotal.value,
        windowStart: winStart,
        windowEnd: winEnd,
        bucketSeconds: 86400,
        abandonmentTimeoutSeconds: 600,
      }
      void refreshStatus()
      startAutoRefresh()
      emit('save', localDist.value)
    }

    const formatWindowDate = (iso: string): string => {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      return d.toLocaleString('en-ZA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    }

    const formatBucketDate = (iso: string): string => {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      return d.toLocaleString('en-ZA', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    // Re-sync local clone when parent passes a new prizeDistribution
    watch(
      () => props.prizeDistribution,
      (incoming) => {
        if (incoming) {
          localDist.value = { ...incoming }
        }
      },
    )

    // Start auto-refresh if already configured
    watch(
      localDist,
      (val) => {
        if (val) {
          startAutoRefresh()
        } else {
          stopAutoRefresh()
        }
      },
      { immediate: false },
    )

    onMounted(() => {
      if (localDist.value) {
        void refreshStatus()
        startAutoRefresh()
      }
    })

    onUnmounted(() => {
      stopAutoRefresh()
    })

    return {
      localDist,
      initTotal,
      status,
      statusLoading,
      statusError,
      refreshStatus,
      enableDistribution,
      formatWindowDate,
      formatBucketDate,
      emit,
    }
  },
})
</script>

<style scoped>
.pdp-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pdp-empty-note {
  font-size: 0.82rem;
  color: var(--ui-inverse-text-muted);
  margin: 0;
}

.pdp-init-row {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.pdp-fields {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.pdp-label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: var(--ui-inverse-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.pdp-input {
  padding: 0.35rem 0.5rem;
  background: var(--ui-inverse-surface-raised, rgba(255, 255, 255, 0.06));
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.375rem;
  color: var(--vt-c-white);
  font-size: 0.88rem;
  width: 100%;
  max-width: 22rem;
}

.pdp-input--short {
  max-width: 8rem;
}

.pdp-window-display {
  font-size: 0.88rem;
  color: var(--vt-c-white);
  text-transform: none;
  letter-spacing: 0;
}

.pdp-field-note {
  font-size: 0.72rem;
  opacity: 0.6;
  text-transform: none;
  letter-spacing: 0;
}

/* Live section */
.pdp-live-section {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.pdp-live-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.2rem;
}

.pdp-live-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ui-inverse-text-muted);
}

.pdp-refresh-btn {
  font-size: 0.78rem;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  color: var(--accent-primary, #6ad0ff);
  padding: 0.15rem 0.5rem;
  cursor: pointer;
}

.pdp-refresh-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.pdp-bucket-label {
  font-size: 0.75rem;
  color: var(--ui-inverse-text-muted);
  margin: 0;
}

.pdp-live-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.88rem;
}

.pdp-live-row span {
  color: var(--ui-inverse-text-muted);
}

.pdp-live-row--muted strong {
  opacity: 0.7;
}

.pdp-status-note {
  font-size: 0.82rem;
  margin: 0;
}

.pdp-status-note--dim {
  color: var(--ui-inverse-text-muted);
}

/* Recent buckets */
.pdp-recent {
  margin-top: 0.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.pdp-recent-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ui-inverse-text-muted);
}

.pdp-recent-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
}

.pdp-recent-date {
  width: 7.5rem;
  flex-shrink: 0;
  color: var(--ui-inverse-text-muted);
}

.pdp-recent-bar {
  flex: 1;
  height: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.25rem;
  overflow: hidden;
  max-width: 8rem;
}

.pdp-recent-fill {
  display: block;
  height: 100%;
  background: var(--accent-primary, #6ad0ff);
  border-radius: 0.25rem;
  transition: width 0.3s ease;
}

.pdp-recent-count {
  width: 3.5rem;
  text-align: right;
  color: var(--ui-inverse-text-muted);
}

/* Actions */
.pdp-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

.pdp-error {
  font-size: 0.82rem;
  color: var(--error-color, #ff6b6b);
  margin: 0;
}

.pdp-toast {
  font-size: 0.82rem;
  color: var(--accent-primary, #6ad0ff);
  margin: 0;
}
</style>
